import metricsStore from '@src/globalStore/metricsStore';
import { getLocalTime } from './utils';
import { humanize } from '@src/utils';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { getMetricDisplayName, getMetricUnitByKey } from '@src/utils/metrics';
import moment from 'moment-timezone';
import { DELETED_DIMENSION, DELETED_METRIC, HIDE_PEAK_VALUE_METRICS } from './constants';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';

const getRootCauseDetails = ({ alert, dimensionListObject = {}, dimensionMapping = {} }) => {
  const { dimensionName, rootCauseDisplayLabel } = alert;

  const rootCauseArray = rootCauseDisplayLabel.split('&&&');
  const dimensionNameArray = dimensionName.split('&&&');
  let rootCauseDisplay = '';
  let areAllRCDimensionsAvailable = true;
  rootCauseArray.forEach((element, index) => {
    const pulse5DimensionMapping = dimensionMapping[dimensionNameArray[index]];

    const dimensionLabel = pulse5DimensionMapping
      ? dimensionListObject[pulse5DimensionMapping.field] || pulse5DimensionMapping.pulse5Label
      : '';

    rootCauseDisplay +=
      index !== 0
        ? ' and ' + `${element} (${dimensionLabel ? dimensionLabel : DELETED_DIMENSION})`
        : `${element} (${dimensionLabel ? dimensionLabel : DELETED_DIMENSION})`;

    if (!dimensionLabel) {
      areAllRCDimensionsAvailable = false;
    }
  });

  return { rootCauseDisplay, areAllRCDimensionsAvailable };
};

// Severity priority for sorting events chronologically (by severity)
// Higher number = higher priority, Ended appears first (most recent), then Critical, Warning, Info
const sortingPriority = {
  Info: 1,
  Warning: 2,
  Critical: 3,
  Ended: 4,
};

const sortByPriority = (array) => {
  return array.sort(
    (alertEvent1, alertEvent2) =>
      sortingPriority[alertEvent2.eventType] - sortingPriority[alertEvent1.eventType],
  );
};

const getAlertEvents = (alert) => {
  const { alertEvents, metricName } = alert;
  const metricUnit = getMetricUnitByKey({
    metricKey: metricName,
  });

  // Sort events by severity priority before processing
  const sortedEvents = sortByPriority([...alertEvents]);

  return sortedEvents.map((e) => ({
    ...e,
    peakSeverity: e.eventType,
    cumulativeImpactedUniquesDisplay: e.cumulativeImpactedUniques
      ? humanize(e.cumulativeImpactedUniques, '')
      : 'NA',
    impactedCountDisplay: e.incrementalImpactedFlows
      ? humanize(e.incrementalImpactedFlows, '')
      : 'NA',
    impactedCountPercentage:
      !!e.incrementalImpactedFlows && !!e.totalFlows
        ? ((e.incrementalImpactedFlows / e.totalFlows) * 100).toFixed(2)
        : 'NA',
    eventTime: e.eventTimestamp ? getLocalTime(new Date(e.eventTimestamp * 1000)) : null,
    peakValue: HIDE_PEAK_VALUE_METRICS.includes(metricName)
      ? 'NA'
      : humanize(Number(e.value), metricUnit),
    peakValueDisplay: HIDE_PEAK_VALUE_METRICS.includes(metricName)
      ? 'NA'
      : humanize(Number(e.value), metricUnit),
    duration: null,
    graphTime: moment.utc(e.eventTimestamp * 1000).format(),
  }));
};

const getAlertDuration = (alert) => {
  const { startTimestamp, endTimestamp } = alert;
  const start = moment(startTimestamp * 1000);
  const end = moment(endTimestamp * 1000);
  const difference = end.diff(start); // The difference in milliseconds
  const duration = moment.duration(difference);
  const durationDisplay =
    difference >= 0
      ? `${duration.hours()}h ${duration.minutes()}m ${duration.seconds()}s`
      : alert.alertType === 'AD_ALERT' || !alert.hasEnded || difference < 0
        ? 'NA'
        : null;

  return durationDisplay;
};

const getAlertStatus = (alert) => {
  const { hasEnded, alertType } = alert;
  return hasEnded !== undefined && hasEnded !== null
    ? alertType === 'AD_ALERT'
      ? 'NA'
      : hasEnded
        ? 'Ended'
        : 'Active'
    : null;
};

export const formatAIAlertSummary = (alerts, dimensionListObject = {}, dimensionMapping = {}) => {
  return (alerts || [])?.map((alert) => {
    const {
      metricName,
      startTimestamp,
      endTimestamp,
      eventTimestamp,
      cumulativeImpactedUniques,
      cumulativeTotalUniques,
      incrementalImpactedFlows,
      totalFlows,
      peakValue,
    } = alert;

    const { allMetricKeys } = metricsStore.getState();
    const isDeletedMetric = !allMetricKeys[CURRENT_PRODUCT_MODE.toLowerCase()].includes(metricName);

    const { rootCauseDisplay, areAllRCDimensionsAvailable } = getRootCauseDetails({
      alert,
      dimensionListObject,
      dimensionMapping,
    });

    const metricUnit = getMetricUnitByKey({
      metricKey: getPercentageMetricByKey({ metricKey: metricName, defaultValue: metricName }),
    });

    return {
      ...alert,
      // Upstream-provided KPI label (e.g. "Conviva Good"), surfaced only when present.
      kpiName: alert?.meta?.kpiName ?? null,
      metricName: isDeletedMetric ? DELETED_METRIC : metricName,
      cumulativeImpactedUniquesDisplay: cumulativeImpactedUniques
        ? humanize(cumulativeImpactedUniques, '')
        : 'NA',
      cumulativeTotalUniquesDisplay: cumulativeTotalUniques
        ? humanize(cumulativeTotalUniques, '')
        : 'NA',
      impactedCountDisplay: incrementalImpactedFlows
        ? humanize(incrementalImpactedFlows, '')
        : 'NA',
      totalCountDisplay: totalFlows ? humanize(totalFlows, '') : 'NA',
      impactedCountPercentage:
        !!incrementalImpactedFlows && !!totalFlows
          ? ((incrementalImpactedFlows / totalFlows) * 100).toFixed(2)
          : 'NA',
      startTime: startTimestamp ? getLocalTime(new Date(startTimestamp * 1000)) : null,
      endTime: endTimestamp ? getLocalTime(new Date(endTimestamp * 1000)) : null,
      startGraphTime: moment.utc(startTimestamp * 1000).format(),
      eventTime: eventTimestamp ? getLocalTime(new Date(eventTimestamp * 1000)) : null,
      rootCauseDisplay,
      metricDisplayName: getMetricDisplayName({
        metricKey: metricName,
        defaultValue: DELETED_METRIC,
      }),
      isDeletedMetric: isDeletedMetric,
      areAllRCDimensionsAvailable,
      metricUnit: metricUnit, // fallback is ''
      peakValueDisplay: HIDE_PEAK_VALUE_METRICS.includes(metricName)
        ? 'NA'
        : humanize(Number(peakValue), metricUnit),
      duration: getAlertDuration(alert),
      alertEvents: getAlertEvents(alert),
      peakEvent: getAlertEvents(alert).find((alertEvent) => alertEvent.peakValue === peakValue),
      status: getAlertStatus(alert),
    };
  });
};
